import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, CheckCircle, Package, Bike, Truck, Home, Phone, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useCountry } from "@/lib/country-context";
import api from "@/lib/api";

// Must match the backend's status pipeline exactly (see backend/models.py Order.status).
const STATUS_STEPS = [
  { key: "confirmed", label: "Order Confirmed", icon: CheckCircle },
  { key: "preparing", label: "Being Prepared", icon: Package },
  { key: "assigned_to_rider", label: "Rider Assigned", icon: Bike },
  { key: "in_transit", label: "Out for Delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

const STATUS_ORDER = STATUS_STEPS.map((s) => s.key);
const LIVE_STATUSES = new Set(["pending", "confirmed", "preparing", "assigned_to_rider", "in_transit"]);
const POLL_MS = 8000;

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCountry();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const fetchOrder = () => {
      api
        .get(`/orders/${id}`)
        .then(({ data }) => {
          if (cancelled) return;
          setOrder(data);
          if (LIVE_STATUSES.has(data.status)) {
            timer = setTimeout(fetchOrder, POLL_MS);
          }
        })
        .catch(() => {
          if (!cancelled) setOrder(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    fetchOrder();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6 space-y-4 max-w-2xl mx-auto">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Order not found</p>
          <Button variant="link" onClick={() => navigate("/orders")}>View all orders</Button>
        </div>
      </div>
    );
  }

  const idx = STATUS_ORDER.indexOf(order.status);
  const cancelled = order.status === "cancelled";

  const fmtTime = (s) => s && new Date(s).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const fmtDate = (s) => new Date(s).toLocaleDateString("en-GB", { weekday: "long", month: "long", day: "numeric" });
  const shortId = (order.id || "").slice(0, 8).toUpperCase();
  const subtotal = (order.total || 0) - (order.delivery_fee || 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center gap-3 p-4 md:p-6 border-b bg-background">
        <button
          onClick={() => navigate("/orders")}
          className="p-2 hover:bg-muted rounded-full transition-colors"
          data-testid="order-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="font-serif text-xl font-bold">Order #{shortId}</h1>
          <p className="text-sm text-muted-foreground">{fmtDate(order.created_at)}</p>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-4">
        {!cancelled ? (
          <div className="bg-card border rounded-2xl p-5">
            <h2 className="font-serif font-bold text-lg mb-5">Order Status</h2>
            <div className="space-y-4">
              {STATUS_STEPS.map((step, i) => {
                const completed = i <= idx;
                const current = i === idx;
                return (
                  <div key={step.key} className="flex items-center gap-4">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      completed ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}>
                      <step.icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${completed ? "text-foreground" : "text-muted-foreground"}`}>
                        {step.label}
                      </p>
                      {current && order.estimated_delivery && step.key !== "delivered" && (
                        <p className="text-xs text-primary mt-0.5">Est. {fmtTime(order.estimated_delivery)}</p>
                      )}
                    </div>
                    {current && (
                      <span className="text-xs font-semibold px-2.5 py-1 bg-primary/10 text-primary rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-destructive/10 border border-destructive/20 rounded-2xl p-5 text-center">
            <p className="font-semibold text-destructive">Order Cancelled</p>
          </div>
        )}

        {order.rider && (
          <div className="bg-card border rounded-2xl p-5" data-testid="order-rider-card">
            <h2 className="font-serif font-bold mb-3">Your Rider</h2>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bike className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate" data-testid="order-rider-name">{order.rider.full_name}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  <span className="capitalize">{order.rider.vehicle_type}</span>
                  {typeof order.rider.rating === "number" && (
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3 fill-current text-primary" />
                      {order.rider.rating.toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
              {order.rider.phone && (
                <a href={`tel:${order.rider.phone}`}
                  className="p-2.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors shrink-0"
                  data-testid="order-rider-call-btn" aria-label="Call rider">
                  <Phone className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        )}

        <div className="bg-card border rounded-2xl p-5 space-y-3">
          <h2 className="font-serif font-bold">Delivery Details</h2>
          <div className="flex items-start gap-3">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-sm">{order.delivery_address}</p>
          </div>
          {order.estimated_delivery && (
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm">Estimated: {fmtTime(order.estimated_delivery)}</p>
            </div>
          )}
        </div>

        <div className="bg-card border rounded-2xl p-5">
          <h2 className="font-serif font-bold mb-4">{order.restaurant_name}</h2>
          <div className="space-y-3">
            {(order.items || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between" data-testid={`order-item-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground w-5">{it.quantity}x</span>
                  <span className="text-sm font-medium">{it.menu_item_name}</span>
                </div>
                <span className="text-sm font-semibold">{formatPrice(it.subtotal)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Delivery Fee</span>
              <span>{formatPrice(order.delivery_fee || 0)}</span>
            </div>
            <div className="flex justify-between font-bold text-base pt-2 border-t">
              <span>Total</span>
              <span data-testid="order-total">{formatPrice(order.total)}</span>
            </div>
          </div>
        </div>

        {order.notes && (
          <div className="bg-card border rounded-2xl p-4">
            <p className="text-sm font-medium text-muted-foreground">Notes</p>
            <p className="text-sm mt-1">{order.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
